import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

import {type AddContentInput} from '#/state/classrooms/types'
import {type ClassroomContent, type ContentKind} from '#/state/classrooms/types'
import {STALE} from '#/state/queries'
import {useAgent} from '#/state/session'
import {classroomProcedure, classroomQuery} from './client'
import {type ClassroomApiError} from './errors'
import {classroomKeys, invalidateClassroom} from './keys'
import {type ContentViewJson, toContent} from './map'

const NS = 'app.taderok.classroom'

export type ClassroomContentPage = {
  content: ClassroomContent[]
  cursor?: string
}

export function useClassroomContentQuery(
  id: string | undefined,
  kind?: ContentKind,
) {
  const agent = useAgent()
  return useInfiniteQuery({
    queryKey: classroomKeys.content(id ?? '', kind),
    queryFn: async ({pageParam}): Promise<ClassroomContentPage> => {
      const res = await classroomQuery<{
        content: ContentViewJson[]
        cursor?: string
      }>(agent, `${NS}.listContent`, {id, kind, cursor: pageParam})
      return {content: res.content.map(toContent), cursor: res.cursor}
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: page => page.cursor,
    staleTime: STALE.SECONDS.THIRTY,
    enabled: !!id,
  })
}

export function useAddClassroomContentMutation(id: string) {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation<
    ClassroomContent,
    ClassroomApiError,
    // The server takes the author from the session
    Omit<AddContentInput, 'author'>
  >({
    mutationFn: async input => {
      const res = await classroomProcedure<{content: ContentViewJson}>(
        agent,
        `${NS}.addContent`,
        {
          id,
          kind: input.kind,
          visibility: input.visibility,
          audience: input.audience,
          text: input.text,
          title:
            input.capsuleTitle ?? input.flashbookTitle ?? input.moduleTitle,
          postUri: input.postUri,
          postCid: input.postCid,
          capsuleUri: input.capsuleUri,
          capsuleCid: input.capsuleCid,
          flashbookUri: input.flashbookUri,
          flashbookCid: input.flashbookCid,
          moduleUri: input.moduleUri,
          moduleCid: input.moduleCid,
        },
      )
      return toContent(res.content)
    },
    onSuccess: added => {
      // Prepend to the unfiltered and matching-kind lists that are loaded
      for (const kind of [undefined, added.kind]) {
        queryClient.setQueryData<InfiniteData<ClassroomContentPage>>(
          classroomKeys.content(id, kind),
          old => {
            if (!old) return old
            const [first, ...rest] = old.pages
            if (old.pages.some(p => p.content.some(c => c.id === added.id))) {
              return old
            }
            return {
              ...old,
              pages: [{...first, content: [added, ...first.content]}, ...rest],
            }
          },
        )
      }
      return invalidateClassroom(queryClient, id)
    },
  })
}

export function useRemoveClassroomContentMutation(id: string) {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation<void, ClassroomApiError, {contentId: string}>({
    mutationFn: async ({contentId}) => {
      await classroomProcedure(agent, `${NS}.removeContent`, {contentId})
    },
    onSuccess: () => invalidateClassroom(queryClient, id),
  })
}

export function useAcknowledgeAnnouncementMutation(id: string) {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation<void, ClassroomApiError, {contentId: string}>({
    mutationFn: async ({contentId}) => {
      await classroomProcedure(agent, `${NS}.acknowledgeAnnouncement`, {
        contentId,
      })
    },
    onSuccess: () => invalidateClassroom(queryClient, id),
  })
}
